import { enableAllPlugins } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import type { AsyncActionHandlers } from 'use-reducer-async';
import FileRequestAPI from '../../../../api/FileRequest';
import ItemAPI from '../../../../api/Item';

import {
  FILE_REQUEST_STATUS,
  MAX_NUMBER_OF_FIELDS,
} from '../../../../constants';

import builderNotificationMessages from '../../../new-file-request-builder/builder-notification/messages';
import {
  addMetadataField,
  removeField,
  toggleFieldRequired,
} from '../../../new-file-request-builder/FormElementMaker';
import {
  getFormElementKey,
  getLayoutAfterFieldColToggle,
  isItemInTwoColumn,
  TOTAL_BORDER_WIDTH,
  updateTwoColItemHeightInLayout,
} from '../../../new-file-request-builder/grid-layout-utils';
import {
  ACTION_CLEAR_METADATA_TEMPLATE,
  ACTION_CLOSE_METADATA_PICKER,
  ACTION_CLOSE_SETTINGS_PANEL,
  ACTION_COPY_FILE_REQUEST,
  ACTION_COPY_FILE_REQUEST_IN_PROGRESS,
  ACTION_DEACTIVATE_BAD_FILE_REQUEST,
  ACTION_DELETE_FILE_REQUEST,
  ACTION_GET_FOLDER_TREE,
  ACTION_GET_FOLDER_TREE_IN_PROGRESS,
  ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
  ACTION_ON_HELP,
  ACTION_ON_UNFOCUS_ELEMENT,
  ACTION_OPEN_FIELD_SELECTOR_DRAWER,
  ACTION_OPEN_METADATA_PICKER,
  ACTION_REMOVE_DESCRIPTION,
  ACTION_REORDER_GROUP_FIELDS,
  ACTION_SEARCH_ITEMS,
  ACTION_SEARCH_ITEMS_IN_PROGRESS,
  ACTION_SELECT_METADATA,
  ACTION_SELECT_METADATA_TEMPLATE,
  ACTION_SET_BUILDER_ERROR,
  ACTION_SET_DESCRIPTION,
  ACTION_SET_DESCRIPTION_EDITABLE,
  ACTION_SET_LABEL,
  ACTION_SET_LIST_OPTIONS_ERROR,
  ACTION_STORE_FORM_ELEMENT_HEIGHT,
  ACTION_TOGGLE_FIELD_COLUMN,
  ACTION_TOGGLE_FILE_REQUEST_ACTIVITY,
  ACTION_TOGGLE_REQUIRED_FIELD,
  ACTION_UPDATE_GROUP_LAYOUT,
  ACTION_UPDATE_ITEM_HEIGHT_IN_LAYOUT,
  ACTION_UPDATE_LIST_OPTIONS,
  ACTION_UPDATE_MAXIMUM_SELECTIONS,
} from '../builder-constant';
import selector from '../builder-selector';
import type {
  BaseAction,
  onDeleteType,
  BuilderReducerType,
} from '../builder-type';
import { calculateNewMaxSelections, willExceedMaxFields } from '../utils';
import { actionToggleFileRequestActivity } from './builder-action.utils';
import { MODAL_ACTIONS } from './modal-action';

// Enable all immer plugins
enableAllPlugins();

type BuilderActionArgs = {
  onDelete: onDeleteType;
};

export const builderActionHandlers = ({
  onDelete,
}: BuilderActionArgs): AsyncActionHandlers<BuilderReducerType, BaseAction> => {
  return {
    [ACTION_OPEN_METADATA_PICKER]: (dispatch, getState) => async action => {
      const state = getState();

      if (willExceedMaxFields(state)) {
        dispatch({
          type: ACTION_SET_BUILDER_ERROR,
          errorMessage: {
            ...builderNotificationMessages.errorMaxFieldsNotification,
            values: { maxCount: MAX_NUMBER_OF_FIELDS },
          },
        });
        return;
      }

      dispatch(action);
    },
    [ACTION_CLOSE_METADATA_PICKER]: dispatch => async action => {
      dispatch(action);
    },
    // Figure out where this is being used? I don't think this is being used anymore
    // ACTION_INSERT_METADATA is used instead and this should be deprecated
    [ACTION_SELECT_METADATA_TEMPLATE]: (dispatch, getState) => async action => {
      const { newMetadataTemplate } = action;
      const state = getState();
      let formContent = selector.getFormContent(state);
      forEach(
        filter(newMetadataTemplate?.fields, field => field.isSelected === true),
        metadataField => {
          formContent = addMetadataField(
            metadataField,
            newMetadataTemplate?.id,
            formContent,
          );
        },
      );
      dispatch({ ...action, formContent });
    },
    [ACTION_CLEAR_METADATA_TEMPLATE]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      let formContent = selector.getFormContent(state);
      forEach(
        filter(formContent.components, component =>
          has(component, 'metadataTemplateId'),
        ),
        component => {
          formContent = removeField(component.id, formContent);
        },
      );
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'cleartemplate',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ ...action, formContent });
    },

    [ACTION_OPEN_FIELD_SELECTOR_DRAWER]:
      (dispatch, getState) => async action => {
        const state = getState();
        const { resin, resinEvent } = state;
        resin.recordAction({
          ...resinEvent,
          component: 'formbuilder',
          target: 'addfield',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
        dispatch(action);
      },

    [ACTION_SELECT_METADATA]: (_, getState) => async () => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilder',
        target: 'addmetadata',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
    },

    [ACTION_TOGGLE_REQUIRED_FIELD]: (dispatch, getState) => async action => {
      const state = getState();
      const { id } = action;
      let formContent = selector.getFormContent(state);
      formContent = toggleFieldRequired(id, formContent);
      const newRequired = get(
        selector.getComponentForId(state, id),
        'required',
      );
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `markasrequired|${newRequired ? 'check' : 'uncheck'}`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch({ ...action, formContent });
    },
    [ACTION_DELETE_FILE_REQUEST]: (dispatch, getState) => async action => {
      const { exitBuilder, navigation, baseUrl, mode } = action;
      const state = getState();
      const { workingFileRequest, resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'delete',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      FileRequestAPI.deleteFileRequest(workingFileRequest).then(() => {
        dispatch({
          type: MODAL_ACTIONS.CLOSE_DELETE_FILE_REQUEST_MODAL,
        });
        if (typeof onDelete === 'function') {
          onDelete();
        }

        exitBuilder(baseUrl, mode, state, navigation.applicationHistory);
      });
    },
    [ACTION_ON_HELP]: (_, getState) => async () => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formbuilderheader|options',
        target: 'gethelp',
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
    },
    [ACTION_ON_UNFOCUS_ELEMENT]: dispatch => async action => {
      dispatch(action);
      dispatch({ type: ACTION_CLOSE_SETTINGS_PANEL });
    },
    [ACTION_DEACTIVATE_BAD_FILE_REQUEST]: (dispatch, getState) => async () => {
      const state = getState();
      if (state.savedFileRequest.status === FILE_REQUEST_STATUS.ACTIVE) {
        await actionToggleFileRequestActivity(dispatch, state);
        dispatch({ type: ACTION_NOTIFY_FILE_REQUEST_INACTIVE });
      }
      dispatch({ type: ACTION_DEACTIVATE_BAD_FILE_REQUEST });
    },
    [ACTION_TOGGLE_FILE_REQUEST_ACTIVITY]: (dispatch, getState) => async () => {
      const state = getState();
      await actionToggleFileRequestActivity(dispatch, state);
    },

    [ACTION_REORDER_GROUP_FIELDS]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      if (resin) {
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: 'fieldreorder, addnewfeild',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
      }
      dispatch(action);
    },

    [ACTION_SET_LABEL]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_UPDATE_LIST_OPTIONS]: dispatch => async action => {
      dispatch(action);
    },
    [ACTION_UPDATE_MAXIMUM_SELECTIONS]:
      (dispatch, getState) => async action => {
        const { id, value } = action;
        const state = getState();
        const numOptions = selector.getOptions(state, id).length;
        const newMaxSelections = calculateNewMaxSelections(value, numOptions);
        dispatch({ ...action, value: newMaxSelections });
      },

    [ACTION_SET_LIST_OPTIONS_ERROR]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_SET_DESCRIPTION]: dispatch => async action => {
      dispatch(action);
    },

    [ACTION_SET_DESCRIPTION_EDITABLE]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `adddescription|check`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch(action);
    },

    [ACTION_REMOVE_DESCRIPTION]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      resin.recordAction({
        ...resinEvent,
        component: 'formelement',
        target: `adddescription|uncheck`,
        data: {
          file_request_url_id: selector.getFileRequestUrlId(state),
        },
      });
      dispatch(action);
    },
    [ACTION_SEARCH_ITEMS]: dispatch => async action => {
      dispatch({ type: ACTION_SEARCH_ITEMS_IN_PROGRESS });
      const { query, isStartingSearch } = action;
      ItemAPI.searchItems(query, isStartingSearch).then(data => {
        dispatch({ ...action, data });
      });
    },

    [ACTION_GET_FOLDER_TREE]: dispatch => async action => {
      dispatch({ type: ACTION_GET_FOLDER_TREE_IN_PROGRESS });
      const { folderId, pageNumber } = action;
      ItemAPI.getFolderTree(folderId, pageNumber).then(data => {
        dispatch({ ...action, data });
      });
    },
    [ACTION_COPY_FILE_REQUEST]: (dispatch, getState) => async action => {
      const state = getState();
      const { workingFileRequest } = state;
      const { selectionItems } = action;
      if (!selectionItems) {
        return;
      }

      dispatch({ type: ACTION_COPY_FILE_REQUEST_IN_PROGRESS });

      FileRequestAPI.copyFileRequest(
        workingFileRequest,
        selectionItems.map(selectionItem => selectionItem.id),
      ).then(data => {
        dispatch({ ...action, data });
      });
    },

    [ACTION_TOGGLE_FIELD_COLUMN]: (dispatch, getState) => async action => {
      const { id, groupId } = action;
      const state = getState();

      const pageInfo = selector.getPageInfoForGroupId(state, groupId);
      if (!pageInfo) {
        return;
      }

      const { layout: formLayout } = pageInfo;
      if (!formLayout) {
        return;
      }

      const newLayout = getLayoutAfterFieldColToggle(formLayout, id);
      dispatch({
        type: ACTION_UPDATE_GROUP_LAYOUT,
        layout: newLayout,
        id: groupId,
      });
    },
    [ACTION_UPDATE_ITEM_HEIGHT_IN_LAYOUT]:
      (dispatch, getState) => async action => {
        const {
          id,
          groupId,
          value: { elementHeight },
        } = action;
        const state = getState();
        const prevElementHeight = get(
          state,
          `formElementsHeightState.${getFormElementKey(groupId, id)}`,
        );

        const pageInfo = selector.getPageInfoForGroupId(state, groupId);
        if (!pageInfo) {
          return;
        }

        const { layout } = pageInfo;
        const newLayout = cloneDeep(layout);
        if (!newLayout) {
          return;
        }

        const itemIndex = newLayout.findIndex(it => it.i === id);
        const itemOnUpdate = newLayout[itemIndex];
        if (
          prevElementHeight === undefined ||
          prevElementHeight !== elementHeight
        ) {
          dispatch({
            type: ACTION_STORE_FORM_ELEMENT_HEIGHT,
            groupId,
            id,
            value: { elementHeight },
          });
        }
        // ignore height change due to on focus border display
        if (
          prevElementHeight !== undefined &&
          Math.abs(elementHeight - prevElementHeight) > TOTAL_BORDER_WIDTH
        ) {
          if (isItemInTwoColumn(itemOnUpdate)) {
            updateTwoColItemHeightInLayout(
              newLayout,
              itemIndex,
              prevElementHeight,
              elementHeight,
              groupId,
              get(state, `formElementsHeightState`, {}),
            );
          }
          dispatch({
            type: ACTION_UPDATE_GROUP_LAYOUT,
            layout: newLayout,
            id: groupId,
          });
        }
      },

    [ACTION_UPDATE_GROUP_LAYOUT]: (dispatch, getState) => async action => {
      const state = getState();
      const { resin, resinEvent } = state;
      if (resin) {
        resin.recordAction({
          ...resinEvent,
          component: 'formelement',
          target: 'draganddrop',
          data: {
            file_request_url_id: selector.getFileRequestUrlId(state),
          },
        });
      }
      dispatch(action);
    },
  };
};
